import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { refetchQueries } from './refetchQueries';
import {
  IResponseData,
  IError,
  IApiParams,
  INotification,
  IMetaData,
  ITeamAssignments,
  IUpdateNotificationStatus,
  IUpdateNotificationProperty,
  IEnableNotification,
  IEnableNotificationAllGroup,
  IChangeUserNotificationStatusPayload,
  IUpdateNotificationStatusAllGroup,
  IUserNotificationSettings,
  NotificationEntityType,
} from 'types';

import { useAuthClient } from './useAuthClient';
import { useAuth, useError } from '../providers';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const notificationSettingsQueryKey = 'notificationSettingsGroup';
const teamAssignmentsQueryKey = 'notificationGroupTeamAssignments';
const userNotificationSettingsQueryKey = 'userNotificationSettingsGroup';
const notificationQueryKey = 'notificationGroup';
const changeUserNotificationStatusQueryKey =
  'changeUserNotificationGroupStatus';
const searchTeamAssignmentsQueryKey = 'searchTeamAssignments';

export function useNotificationSettingsGroupApi({
  queryParameters,
  organizationId,
  entityType,
}: IApiParams) {
  const authClient = useAuthClient();
  const queryClient = useQueryClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getNotificationSettingsEntityGroupURL = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/get-notification-settings`;
  }, [entityType, currentSupplierId]);

  const getNotificationSettingsAssignmentsGroupURL = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/get-notification-settings-assignments`;
  }, [entityType, currentSupplierId]);

  const changeNotificationStatusUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/change-status`;
  }, [entityType, currentSupplierId]);

  const changeNotificationPropertyUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/change-property-value`;
  }, [currentSupplierId, entityType]);

  const changeUsersStatusOrgNotificationUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/change-notification-assignment-status`;
  }, [currentSupplierId, entityType]);

  const changeNotificationStatusAllGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/change-status-all-group`;
  }, [currentSupplierId, entityType]);

  const enableNotificationUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/enable`;
  }, [entityType, currentSupplierId]);

  const enableNotificationAllUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/enable-all-group`;
  }, [currentSupplierId, entityType]);

  const getUserNotificationSettingsAssignmentsURL = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${entityType}/get-user-notification-settings-assignments`;
  }, [entityType, currentSupplierId]);

  const {
    data: notificationSettingsEntityGroupResponse,
    refetch: fetchNotificationSettingsEntityGroup,
    isFetching: isFetchingNotificationSettingsEntityGroup,
    error: fetchNotificationSettingsEntityGroupError,
  } = useQueryWithErrorHandler<IResponseData<INotification[]>, IError>({
    queryKey: [notificationSettingsQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getNotificationSettingsEntityGroupURL}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.getEntityGroupNotificationSettings,
        error,
        {
          getNotificationSettingsEntityGroupURL,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    data: userGroupNotificationSettingsResponse,
    refetch: fetchUserGroupNotificationSettings,
    isFetching: isFetchingUserGroupNotificationSettings,
    error: fetchUserGroupNotificationSettingsError,
  } = useQueryWithErrorHandler<
    IResponseData<IUserNotificationSettings>,
    IError
  >({
    queryKey: [userNotificationSettingsQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getUserNotificationSettingsAssignmentsURL}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.getUserGroupNotificationSettings,
        error,
        {
          getUserNotificationSettingsAssignmentsURL,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    data: notificationSettingsGroupAssignmentsResponse,
    refetch: fetchNotificationSettingsGroupAssignments,
    isFetching: isFetchingNotificationSettingsGroupAssignments,
    error: fetchNotificationSettingsGroupAssignmentsError,
  } = useQueryWithErrorHandler<IResponseData<ITeamAssignments[]>, IError>({
    queryKey: [teamAssignmentsQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getNotificationSettingsAssignmentsGroupURL}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.getEntityGroupUserNotificationAssignments,
        error,
        {
          getNotificationSettingsAssignmentsGroupURL,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    mutateAsync: changeNotificationStatusEntityGroup,
    isPending: isChangingNotificationStatusEntityGroup,
    error: changeNotificationStatusEntityGroupError,
  } = useMutation({
    mutationFn: (payload: IUpdateNotificationStatus) =>
      authClient(changeNotificationStatusUrl, {
        method: 'PATCH',
        queryParameters,
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationQueryKey],
      });
      return queryClient.getQueryData<IResponseData<INotification>>([
        notificationQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<INotification>>(
          [notificationQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.changeEntityGroupNotificationStatus,
        error,
        {
          changeNotificationStatusUrl,
          ...variables,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationQueryKey],
      });
    },
  });

  const {
    mutateAsync: enableNotificationEntityGroup,
    isPending: isEnablingNotificationEntityGroup,
    error: enableNotificationEntityGroupError,
  } = useMutation({
    mutationFn: (payload: IEnableNotification) =>
      authClient(enableNotificationUrl, {
        method: 'PATCH',
        data: payload,
        queryParameters,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationQueryKey],
      });
      return queryClient.getQueryData<IResponseData<INotification>>([
        notificationQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<INotification>>(
          [notificationQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(errors.enableEntityNotification, error, {
        enableNotificationUrl,
        ...variables,
      });
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationQueryKey],
      });
    },
  });

  const {
    mutateAsync: enableNotificationAllGroup,
    isPending: isEnablingNotificationAllGroup,
  } = useMutation({
    mutationFn: (payload: IEnableNotificationAllGroup) =>
      authClient(enableNotificationAllUrl, {
        method: 'PATCH',
        data: payload,
        queryParameters,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationSettingsQueryKey],
      });
      return queryClient.getQueryData<IResponseData<INotification[]>>([
        notificationSettingsQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<INotification[]>>(
          [notificationSettingsQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        queryParameters?.description
          ? errors.enableEntityNotificationAllGroup
          : errors.enableEntityNotificationAll,
        error,
        {
          enableNotificationAllUrl,
          ...variables,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationQueryKey],
      });
    },
  });

  const {
    mutateAsync: changeNotificationStatusAllGroup,
    isPending: isChangingNotificationStatusAllGroup,
  } = useMutation({
    mutationFn: (payload: IUpdateNotificationStatusAllGroup) =>
      authClient(changeNotificationStatusAllGroupUrl, {
        method: 'PATCH',
        data: payload,
        queryParameters,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationSettingsQueryKey],
      });
      return queryClient.getQueryData<IResponseData<INotification[]>>([
        notificationSettingsQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<INotification[]>>(
          [notificationSettingsQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.changeEntityGroupNotificationStatusAllGroup,
        error,
        {
          changeNotificationStatusAllGroupUrl,
          ...variables,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationQueryKey],
      });
    },
  });

  const {
    mutateAsync: changeNotificationPropertyEntityGroup,
    isPending: isChangingNotificationPropertyEntityGroup,
    error: changeNotificationPropertyEntityGroupError,
  } = useMutation({
    mutationFn: (payload: IUpdateNotificationProperty) =>
      authClient(changeNotificationPropertyUrl, {
        method: 'PATCH',
        queryParameters,
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationQueryKey],
      });
      return queryClient.getQueryData<IResponseData<INotification>>([
        notificationQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<INotification>>(
          [notificationQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.changeEntityGroupNotificationPropertyValue,
        error,
        {
          changeNotificationPropertyUrl,
          ...variables,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationQueryKey],
      });
    },
  });

  const {
    data: searchTeamAssignmentsGroupResponse,
    refetch: fetchSearchTeamAssignmentsGroup,
    isFetching: isFetchingSearchTeamAssignmentsGroup,
    error: fetchSearchTeamAssignmentsGroupError,
  } = useQueryWithErrorHandler<IResponseData<ITeamAssignments[]>, IError>({
    queryKey: [searchTeamAssignmentsQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getNotificationSettingsAssignmentsGroupURL}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.getEntityGroupUserNotificationAssignments,
        error,
        {
          getNotificationSettingsAssignmentsGroupURL,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    data: changeUsersStatusNotificationGroupResponse,
    mutateAsync: changeUsersStatusNotificationGroup,
    isPending: isChangingUsersStatusNotificationGroup,
    error: changeUsersStatusNotificationGroupError,
    isSuccess: isChangingUsersStatusNotificationGroupSuccess,
  } = useMutation({
    mutationFn: (payload: {
      users: IChangeUserNotificationStatusPayload[];
      type?: NotificationEntityType;
      groupId?: string;
    }) =>
      authClient(
        payload.type
          ? `/api/v1/${currentSupplierId}/NotificationSettingsGroup/${payload.type}/change-notification-assignment-status`
          : changeUsersStatusOrgNotificationUrl,
        {
          method: 'POST',
          data: payload.users,
          queryParameters: {
            ...queryParameters,
            groupId: payload.groupId || queryParameters?.groupId,
          },
        },
      ),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [changeUserNotificationStatusQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        changeUserNotificationStatusQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<any>>(
          [changeUserNotificationStatusQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.changeEntityGroupUserNotificationAssignments,
        error,
        {
          changeUserNotificationStatusQueryKey,
          variables: JSON.stringify(variables),
        },
      );
      refetchQueries(queryClient, {
        queryKey: [teamAssignmentsQueryKey],
      });
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [changeUserNotificationStatusQueryKey],
      });
    },
  });

  const notificationSettingsEntityGroup = useMemo<INotification[] | undefined>(
    () => notificationSettingsEntityGroupResponse?.data,
    [notificationSettingsEntityGroupResponse],
  );

  const notificationSettingsAssignments = useMemo<
    ITeamAssignments[] | undefined
  >(
    () => notificationSettingsGroupAssignmentsResponse?.data,
    [notificationSettingsGroupAssignmentsResponse],
  );

  const notificationSettingsAssignmentsMetadata = useMemo<
    IMetaData | undefined
  >(
    () => notificationSettingsGroupAssignmentsResponse?.metadata,
    [notificationSettingsGroupAssignmentsResponse],
  );

  const searchTeamAssignmentsGroup = useMemo<ITeamAssignments[] | undefined>(
    () => searchTeamAssignmentsGroupResponse?.data,
    [searchTeamAssignmentsGroupResponse],
  );

  const userGroupNotificationSettings = useMemo<
    IUserNotificationSettings | undefined
  >(
    () => userGroupNotificationSettingsResponse?.data,
    [userGroupNotificationSettingsResponse],
  );

  return {
    notificationSettingsEntityGroup,
    fetchNotificationSettingsEntityGroup,
    isFetchingNotificationSettingsEntityGroup,
    fetchNotificationSettingsEntityGroupError,
    fetchNotificationSettingsGroupAssignments,
    isFetchingNotificationSettingsGroupAssignments,
    fetchNotificationSettingsGroupAssignmentsError,
    notificationSettingsAssignments,
    notificationSettingsAssignmentsMetadata,
    changeNotificationStatusEntityGroup,
    isChangingNotificationStatusEntityGroup,
    changeNotificationStatusEntityGroupError,
    changeNotificationPropertyEntityGroup,
    isChangingNotificationPropertyEntityGroup,
    changeNotificationPropertyEntityGroupError,
    changeUsersStatusNotificationGroupResponse,
    changeUsersStatusNotificationGroup,
    isChangingUsersStatusNotificationGroup,
    changeUsersStatusNotificationGroupError,
    isChangingUsersStatusNotificationGroupSuccess,
    fetchSearchTeamAssignmentsGroup,
    isFetchingSearchTeamAssignmentsGroup,
    fetchSearchTeamAssignmentsGroupError,
    searchTeamAssignmentsGroup,
    changeNotificationStatusAllGroup,
    isChangingNotificationStatusAllGroup,
    fetchUserGroupNotificationSettings,
    isFetchingUserGroupNotificationSettings,
    fetchUserGroupNotificationSettingsError,
    userGroupNotificationSettings,
    enableNotificationEntityGroup,
    isEnablingNotificationEntityGroup,
    enableNotificationEntityGroupError,
    enableNotificationAllGroup,
    isEnablingNotificationAllGroup,
  };
}
